import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useErrorContext } from '@qpr/client-library';
import React, { useEffect, useState } from 'react';
import { isValidId } from '../../../utils/url-utils';
import BusinessUnitCard from '../components/BusinessUnitCard';
import initialState from '../corporate-business-canvas-state';
import { CBCViewModelType } from './corporate-business-canvas-view-model';

const PREFIX = 'CorporateBusinessCanvasView';

const classes = {
  pagetitle: `${PREFIX}-pagetitle`,
  pagesubtitle: `${PREFIX}-pagesubtitle`
};

const StyledContainer = styled(Container)({
  [`& .${classes.pagetitle}`]: {
    marginTop: '38px'
  },
  [`& .${classes.pagesubtitle}`]: {
    marginTop: '16px',
    marginBottom: '32px',
    fontWeight: 400
  }
});

interface CorporateBusinessCanvasViewProps {
  CBCViewModel: CBCViewModelType;
}

const CorporateBusinessCanvasView = ({ CBCViewModel }: CorporateBusinessCanvasViewProps) => {
  const viewModel: CBCViewModelType = CBCViewModel;

  const [state, setState] = useState(initialState);
  const [inited, setInited] = useState(false);

  const { setError } = useErrorContext();

  useEffect(() => {
    const initData = async () => {
      if (!isValidId(viewModel.getId())) {
        setInited(true);
        setError({
          message: 'Invalid parameter.',
          details: `element id malformed ${viewModel.getId()}`
        });
        return;
      }

      setError(null);
      const data = await viewModel.getData();
      setState(data);
      setInited(true);
      if (data.error.message) setError(data.error);
    };
    initData();
  }, [viewModel.getId()]);

  return (
    <StyledContainer data-testid={inited ? 'cbc-main' : 'loading'}>
      {inited ? (
        <>
          <Typography
            variant="h2"
            gutterBottom
            align="center"
            className={`${classes.pagetitle} customHeader`}
            data-testid="cbc-title"
          >
            {state.titles.title}
          </Typography>
          <Typography
            variant="h6"
            gutterBottom
            align="center"
            className={`${classes.pagesubtitle} customHeader`}
            data-testid="cbc-subtitle"
          >
            {state.titles.subtitle}
          </Typography>
          <Grid container spacing={2} alignItems="stretch" data-testid="cbc-cards">
            {state.businessUnits.map(businessUnit => (
              <Grid item key={businessUnit.id} xs sm md lg>
                <BusinessUnitCard inited={inited} item={businessUnit} />
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <Skeleton
            style={{ margin: '0 auto' }}
            component="div"
            variant="text"
            width={400}
            height={100}
          />
          <Skeleton
            style={{ margin: '0 auto' }}
            component="div"
            variant="text"
            width={600}
            height={50}
          />
          <Skeleton
            style={{ display: 'inline-block', margin: '8px' }}
            component="div"
            variant="rectangular"
            width={384}
            height={132}
          />
          <Skeleton
            style={{ display: 'inline-block', margin: '8px' }}
            component="div"
            variant="rectangular"
            width={384}
            height={132}
          />
          <Skeleton
            style={{ display: 'inline-block', margin: '8px' }}
            component="div"
            variant="rectangular"
            width={384}
            height={132}
          />
        </div>
      )}
    </StyledContainer>
  );
};

export default CorporateBusinessCanvasView;
